<template>
	<page title="Company Statistics">
		<template #actions>
			<v-tooltip left>
				<template #activator="{ on, attrs }">
					<v-avatar
						color="primary"
						class="cursor-pointer"
						dark
						large
						v-bind="attrs"
						@click="forceReload"
						v-on="on">
						<v-icon color="white">mdi-refresh</v-icon>
					</v-avatar>
				</template>
				<span>Stats are cached, recalculate?</span>
			</v-tooltip>
			<v-select
				v-model="selectedYear"
				:items="years"
				filled
				hide-details
				dense
				class="cursor-pointer"
				background-color="white"
				label="Stats by Year"
				@input="reloadStats" />
		</template>

		<v-row>
			<v-col cols="12" sm="6" md="4" class="pt-sm-0">
				<v-sheet rounded>
					<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader" :loading="loading">
						<v-card-title class="align-center">
							<v-icon left dark>mdi-bank</v-icon>
							<h3 class="white--text">Revenue Statistics</h3>
							<v-spacer />
						</v-card-title>
						<v-divider />
						<v-list>
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">{{ year }} Invoices <strong>Outstanding</strong></span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ revenue.ytd_outstanding | money }}</div>
								</v-list-item-action>
							</v-list-item>

							<v-divider />

							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">{{ year }} Invoices Fully <strong>Paid</strong></span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ revenue.ytd_paid | money }}</div>
								</v-list-item-action>
							</v-list-item>

							<v-divider />

							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">{{ year }} Invoiced <strong>Total</strong></span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ revenue.ytd_total | money }}</div>
								</v-list-item-action>
							</v-list-item>

							<v-divider />

							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">{{ year }} Payments <strong>Received</strong></span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ revenue.ytd_payments | money }}</div>
								</v-list-item-action>
							</v-list-item>

							<v-divider />

							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">{{ year }} Invoices <strong>Drafted</strong></span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ revenue.ytd_draft | money }}</div>
								</v-list-item-action>
							</v-list-item>
						</v-list>
					</v-card>
				</v-sheet>
			</v-col>
			<v-col cols="12" sm="6" md="4" class="pt-sm-0">
				<v-sheet rounded>
					<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader" :loading="loading">
						<v-card-title class="align-center">
							<v-icon left dark>mdi-chart-pie-outline</v-icon>
							<h3 class="white--text">System Overview</h3>
							<v-spacer />
						</v-card-title>
						<v-divider />
						<v-list>
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Projects</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.projects.all | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
							<v-divider />
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Properties</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.properties.all | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
							<v-divider />
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Clients</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.clients.all | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
							<v-divider />
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Users</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.users.all | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
						</v-list>
					</v-card>
				</v-sheet>
			</v-col>
			<v-col cols="12" sm="6" md="4" class="pt-sm-0">
				<v-sheet rounded>
					<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader" :loading="loading">
						<v-card-title class="align-center">
							<v-icon left dark>mdi-chart-pie-outline</v-icon>
							<h3 class="white--text">YTD System Overview</h3>
							<v-spacer />
						</v-card-title>
						<v-divider />
						<v-list>
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Projects</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.projects.ytd | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
							<v-divider />
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Properties</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.properties.ytd | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
							<v-divider />
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Clients</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.clients.ytd | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
							<v-divider />
							<v-list-item>
								<v-list-item-content>
									<span class="text-h6 text--secondary">Users</span>
								</v-list-item-content>
								<v-list-item-action>
									<div class="text-h6 font-mono text--primary">{{ counts.users.ytd | numeric }}</div>
								</v-list-item-action>
							</v-list-item>
						</v-list>
					</v-card>
				</v-sheet>
			</v-col>
		</v-row>
	</page>
</template>
<script>
import Backend from '@/Layouts/Backend'
import Page from '@/Pages/Backend/Components/Page'

export default {
	components: { Page },
	layout: Backend,
	props: {
		counts: {
			type: Object,
			required: true
		},
		revenue: {
			type: Object,
			required: true
		},
		years: {
			type: Array,
			required: true
		},
		year: {
			type: Number,
			required: false,
			default: () => null
		}
	},
	data () {
		return {
			selectedYear: this.year,
			loading: false
		}
	},
	methods: {
		forceReload () {
			this.$inertia.reload({
				data: { year: this.year, force : true },
				only: ['counts', 'revenue'],
				onBefore: () => this.loading = true,
				onSuccess: () => this.loading = false
			})
		},
		reloadStats (selectedYear) {
			const url = this.route('stats.company')
			this.$inertia.visit(url, {
				data: { year: selectedYear },
				onBefore: () => this.loading = true,
				onSuccess: () => this.loading = false
			})
		}
	}
}
</script>
